// redux/slices/customerAuthSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import PublicOrderingServices from "../services/PublicOrderingServices";
import PublicOrderingServices from "../../services/PublicOrderingServices.js";

// ----------- REGISTER -----------
export const registerCustomer = createAsyncThunk(
  "customerAuth/register",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await PublicOrderingServices.registerCustomer(payload);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Registration failed");
    }
  }
);

// ----------- REGISTER WITH PHONE NUMBER -----------
export const registerCustomerPhoneNumber = createAsyncThunk(
  "customerAuth/registerPhoneNumber",
  async (phoneNumber, { rejectWithValue }) => {
    try {
      const res = await PublicOrderingServices.registerCustomerPhoneNumber(phoneNumber);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Phone registration failed");
    }
  }
);

// ----------- LOGIN -----------
export const customerLogin = createAsyncThunk(
  "customerAuth/login",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await PublicOrderingServices.customerLogin(payload);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Login failed");
    }
  }
);

// ----------- PHONE LOGIN -----------
export const customerPhoneLogin = createAsyncThunk(
  "customerAuth/phoneLogin",
  async (phoneNumber, { rejectWithValue }) => {
    try {
      const res = await PublicOrderingServices.customerPhoneLogin(phoneNumber);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Phone login failed");
    }
  }
);

// ----------- FORGOT PASSWORD -----------
export const forgotPassword = createAsyncThunk(
  "customerAuth/forgotPassword",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await PublicOrderingServices.customerAuthForgotPassword(payload);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Forgot password failed");
    }
  }
);

// ----------- EMAIL OTP LOGIN -----------
export const emailOtpLogin = createAsyncThunk(
  "customerAuth/emailOtpLogin",
  async (email, { rejectWithValue }) => {
    try {
      const res = await PublicOrderingServices.customerAuthForgotPassword({ emailAddress: email });
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Email OTP login failed");
    }
  }
);

// ----------- VERIFY OTP -----------
export const verifyOtp = createAsyncThunk(
  "customerAuth/verifyOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await PublicOrderingServices.verifyOtp(payload);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "OTP verification failed");
    }
  }
);

// ----------- RESET PASSWORD -----------
export const resetPassword = createAsyncThunk(
  "customerAuth/resetPassword",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await PublicOrderingServices.customerAuthResetPassword(payload);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Reset password failed");
    }
  }
);

const customerAuthSlice = createSlice({
  name: "customerAuth",
  initialState: {
    customer: null,
    token: null,
    emailForOtp: null,
    phoneForOtp: null,
    isPhoneRegistration: false,
    isPhoneLogin: false,
    isEmailLoginOtp: false,
    loading: false,
    pendingReset: false,
    error: null,
    otpVerified: false,
    userId: null,
    customerId: null,
    confirmationToken: null,
  },

  reducers: {
    logoutCustomer: (state) => {
      state.customer = null;
      state.token = null;
      localStorage.removeItem("customerToken");
    },
    setEmailForOtp: (state, action) => {
      state.emailForOtp = action.payload;
      state.isPhoneRegistration = false;
      state.isPhoneLogin = false;
    },
    setPhoneForOtp: (state, action) => {
      state.phoneForOtp = action.payload;
      state.isPhoneRegistration = true;
      state.isPhoneLogin = false;
    },
    setPhoneForLoginOtp: (state, action) => {
      state.phoneForOtp = action.payload;
      state.isPhoneRegistration = false;
      state.isPhoneLogin = true;
    },
    setEmailForLoginOtp: (state, action) => {
      state.emailForOtp = action.payload;
      state.isPhoneRegistration = false;
      state.isPhoneLogin = false;
      state.isEmailLoginOtp = true;
      state.pendingReset = false;
    },
  },

  extraReducers: (builder) => {
    builder
      // REGISTER
      .addCase(registerCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.userId = action.payload.userId;
        state.customerId = action.payload.customerId;
        state.confirmationToken = action.payload.confirmationToken;
      })
      .addCase(registerCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // REGISTER PHONE NUMBER
      .addCase(registerCustomerPhoneNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerCustomerPhoneNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.userId = action.payload.userId;
        state.customerId = action.payload.customerId;
        state.confirmationToken = action.payload.confirmationToken;
      })
      .addCase(registerCustomerPhoneNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // LOGIN
      .addCase(customerLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(customerLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.customer = action.payload.customer;
        state.token = action.payload.token;

        localStorage.setItem("customerToken", action.payload.token);
      })
      .addCase(customerLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // PHONE LOGIN
      .addCase(customerPhoneLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(customerPhoneLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.customer = action.payload.customer;
        state.token = action.payload.token;

        localStorage.setItem("customerToken", action.payload.token);
      })
      .addCase(customerPhoneLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // FORGOT PASSWORD
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        // store the email used for forgot flow so VerifyOtp can reference it
        state.emailForOtp = (action.meta && action.meta.arg && action.meta.arg.emailAddress) || action.payload?.emailAddress || null;
        state.pendingReset = true;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // EMAIL OTP LOGIN
      .addCase(emailOtpLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(emailOtpLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.emailForOtp = action.meta.arg;
        state.isEmailLoginOtp = true;
        state.pendingReset = false;
      })
      .addCase(emailOtpLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // OTP VERIFY
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.otpVerified = true;
        if (action.payload?.token) {
          state.token = action.payload.token;
          state.customer = action.payload.customer;
          localStorage.setItem("customerToken", action.payload.token);
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
      
      // RESET PASSWORD handlers
      builder
        .addCase(resetPassword.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(resetPassword.fulfilled, (state, action) => {
          state.loading = false;
          state.pendingReset = false;
        })
        .addCase(resetPassword.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        });
  },
});

export const { logoutCustomer, setEmailForOtp, setPhoneForOtp, setPhoneForLoginOtp, setEmailForLoginOtp } = customerAuthSlice.actions;
export default customerAuthSlice.reducer;
